import { 
  Alert, 
  AlertTitle, 
  Box, 
  CircularProgress, 
  InputAdornment, 
  TextField,
  Container,
  Typography,
  Button,
  Link,
  Divider
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { isLogged, saveToken } from "../service/auth";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import InfoAlert from "../components/InfoAlert";
import themeNutriFast from "../styles/NutriFastColors";
import { useForm } from "react-hook-form";
import { 
  AccountCircle, 
  Lock, 
  CheckCircle, 
  Cancel, 
  LockReset, 
  PersonAdd 
} from "@mui/icons-material";
import { ProgressButton } from "../components/MicroInteractions";
import { SuccessRipple } from "../components/FeedbackNotification";
import { TouchFeedback, PulseEffect } from "../components/RippleEffect";

export default function Login(props) {
  const [loading, setLoading] = useState(false);
  const [showMessage, setShowMessage] = useState(true);
  const [serverError, setServerError] = useState("");
  const [loginSuccess, setLoginSuccess] = useState(false);
  let { state } = useLocation();
  let navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
    watch,
  } = useForm({
    mode: "onChange", // Validation en temps réel
    defaultValues: {
      username: "",
      password: "",
    },
  });

  useEffect(() => {
    if (isLogged()) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const onSubmit = async (credentials) => {
    setLoading(true);
    setServerError("");
    setLoginSuccess(false);
    
    try {
      const { data } = await axios.post(process.env.REACT_APP_LOGIN_URL, { credentials });
      setLoginSuccess(true);
      saveToken(data.acces_token);
      
      // Petit délai pour montrer l'animation de succès
      setTimeout(() => {
        navigate("/dashboard");
      }, 1000);
    } catch (err) {
      setServerError("Mot de passe ou identifiant erroné");
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, loginSuccess ? 1000 : 0);
    }
  };

  const handleCloseInfoAlert = () => {
    setShowMessage(false);
  };

  // Pour surveiller les valeurs en temps réel
  const watchUsername = watch("username");
  const watchPassword = watch("password");

  const getFieldStatus = (fieldName) => {
    if (!touchedFields[fieldName]) return null;
    if (errors[fieldName]) return "error";
    if (fieldName === "username" && watchUsername) return "success";
    if (fieldName === "password" && watchPassword) return "success";
    return null;
  };

  const getFieldIcon = (fieldName) => {
    const status = getFieldStatus(fieldName);
    if (status === "success") return <CheckCircle color="success" />;
    if (status === "error") return <Cancel color="error" />;
    return null;
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, md: 4 } }}>
      {state && state.loggedOut && showMessage && (
        <InfoAlert message={state.message} onClose={handleCloseInfoAlert} />
      )}

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: 3,
          p: { xs: 3, md: 4 },
          mt: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: 500,
          mx: 'auto'
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{ 
            textAlign: 'center',
            color: themeNutriFast.palette.primary.dark,
            fontWeight: 600,
            mb: 3
          }}
        >
          Connexion
        </Typography>
        
        <Box sx={{ mb: 3, width: '100%' }}>
          <TextField
                  fullWidth
                  label="Pseudo"
                  placeholder="Saisissez votre pseudo"
                  variant="outlined"
                {...register("username", {
                  required: "Le pseudo est obligatoire",
                  minLength: {
                    value: 2,
                    message: "Le pseudo doit contenir au moins 2 caractères",
                  },
                })}
                error={!!errors.username}
                helperText={errors.username?.message}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircle />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {getFieldIcon("username")}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: getFieldStatus("username") === "success" 
                        ? "success.main" 
                        : getFieldStatus("username") === "error" 
                        ? "error.main" 
                        : "primary.main",
                    },
                  },
                }}
                />
            </Box>

            <Box sx={{ mb: 3, width: '100%' }}>
                <TextField
                  fullWidth
                  type="password"
                  label="Mot de passe"
                  placeholder="Saisissez le mot de passe"
                  variant="outlined"
                {...register("password", {
                  required: "Le mot de passe est obligatoire",
                })}
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {getFieldIcon("password")}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: getFieldStatus("password") === "success" 
                        ? "success.main" 
                        : getFieldStatus("password") === "error" 
                        ? "error.main" 
                        : "primary.main",
                    },
                  },
                }}
                />
            </Box>

            {serverError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>{serverError}</AlertTitle>
              </Alert>
            )}

            <Box sx={{ mb: 3, width: '100%' }}>
              <SuccessRipple trigger={loginSuccess}>
                <TouchFeedback 
                  disabled={!isValid || loading}
                  scaleDown={0.95}
                  scaleUp={1.02}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={!isValid || loading}
                    sx={{
                      backgroundColor: loginSuccess 
                        ? '#4caf50' 
                        : themeNutriFast.palette.primary.dark,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      transform: loading ? 'scale(0.98)' : 'scale(1)',
                      '&:hover': {
                        backgroundColor: loginSuccess 
                          ? '#45a049' 
                          : themeNutriFast.palette.primary.main,
                      },
                      '&:disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                        color: 'rgba(0, 0, 0, 0.26)'
                      }
                    }}
                  >
                    {loginSuccess ? (
                      <>
                        <CheckCircle 
                          size={20} 
                          style={{ marginRight: 8 }}
                        />
                        Connexion réussie !
                      </>
                    ) : loading ? (
                      <>
                        <CircularProgress
                          size={20}
                          color="inherit"
                          style={{ marginRight: 8 }}
                        />
                        Connexion en cours...
                      </>
                    ) : (
                      "Se connecter"
                    )}
                  </Button>
                </TouchFeedback>
              </SuccessRipple>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: 2, 
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%'
            }}>
              <TouchFeedback scaleDown={0.98} scaleUp={1.02}>
                <Button
                  component={RouterLink}
                  to="/forget"
                  variant="text"
                  startIcon={<LockReset />}
                  sx={{
                    color: themeNutriFast.palette.primary.main,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    minWidth: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      backgroundColor: 'rgba(2, 62, 138, 0.04)',
                    }
                  }}
                >
                  Mot de passe oublié
                </Button>
              </TouchFeedback>

              <TouchFeedback scaleDown={0.98} scaleUp={1.02}>
                <Button
                  component={RouterLink}
                  to="/formulaire"
                  variant="outlined"
                  startIcon={<PersonAdd />}
                  sx={{
                    borderColor: themeNutriFast.palette.primary.main,
                    color: themeNutriFast.palette.primary.main,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                    minWidth: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      borderColor: themeNutriFast.palette.primary.dark,
                      backgroundColor: 'rgba(2, 62, 138, 0.04)',
                    }
                  }}
                >
                  Je m'inscris
                </Button>
              </TouchFeedback>
            </Box>
      </Box>
    </Container>
  );
}